import React, { FunctionComponent, useCallback, useEffect, useRef, useState } from 'react';
import Card from '../../../Card/Card';
import Button from '../../../../design-system/Button/Button';
import { H5 } from '../../../../design-system/Headings/Headings';

const HS_SCRIPT_SRC_BASE = 'https://js.hsforms.net/forms/embed/developer';
const HS_PORTAL_ID = '2052727';
const HS_FORM_ID = '2ac1d176-c6a2-4f60-a053-fc3fb55e6c5e';

const HS_FIELD_API_ENTITY_ID = '0-1/context_plugin_api_entity_id';
const HS_FIELD_EMAIL = '0-1/email';

declare global {
    interface Window {
        HubSpotFormsV4?: {
            getFormFromEvent: (event: Event) => {
                getFormId: () => string;
                setFieldValue: (name: string, value: string) => void;
            };
        };
    }
}

const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
        <path d="M2 7l10 7 10-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

function reloadEmbedScript(): void {
    document.querySelectorAll(`script[data-hs-embed]`).forEach((s) => s.remove());
    const script = document.createElement('script');
    script.src = `${HS_SCRIPT_SRC_BASE}/${HS_PORTAL_ID}.js`;
    script.defer = true;
    script.setAttribute('data-hs-embed', 'true');
    document.head.appendChild(script);
}

interface FreeSetupCardProps {
    apiEntityId?: string;
    userEmail?: string;
}

export const FreeSetupCard: FunctionComponent<FreeSetupCardProps> = ({
    apiEntityId,
    userEmail,
}) => {
    const [showForm, setShowForm] = useState(false);
    const propsRef = useRef({ apiEntityId, userEmail });
    propsRef.current = { apiEntityId, userEmail };

    const handleClick = useCallback(() => {
        setShowForm(true);
    }, []);

    useEffect(() => {
        const onFormReady = (event: Event) => {
            if (!window.HubSpotFormsV4) return;
            const form = window.HubSpotFormsV4.getFormFromEvent(event);
            if (form.getFormId() !== HS_FORM_ID) return;

            const { apiEntityId: entityId, userEmail: email } = propsRef.current;
            if (entityId) form.setFieldValue(HS_FIELD_API_ENTITY_ID, entityId);
            if (email) form.setFieldValue(HS_FIELD_EMAIL, email);
        };

        window.addEventListener('hs-form-event:on-ready', onFormReady);

        const timer = setTimeout(() => reloadEmbedScript(), 50);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('hs-form-event:on-ready', onFormReady);
        };
    }, []);

    return (
        <Card className="mt-4 publishing-sdk-card greenBorder">
            <div className="flex items-start justify-between mb-3">
                <H5>Let Us Handle the Setup</H5>
            </div>
            <p className="text-14 text-goose-800 mb-4">
                During our launch period, the APIMatic team is offering free concierge setup for
                Context Plugins. Send us a message and we&apos;ll take it from there.
            </p>

            {!showForm && (
                <Button
                    text="Set Them Up For Me ›"
                    fill="primary"
                    icon={<MailIcon />}
                    onClick={handleClick}
                />
            )}
            <div style={{ display: showForm ? 'block' : 'none' }}>
                <div
                    className="hs-form-html"
                    data-region="na1"
                    data-form-id={HS_FORM_ID}
                    data-portal-id={HS_PORTAL_ID}
                />
            </div>
        </Card>
    );
};
